import {
	setBoardDetailViewMode,
	getBoardDetailViewMode,
} from '@atlassian/jira-software-board-common/src';
import {
	DETAIL_VIEW_SIDEBAR,
	DETAIL_VIEW_MODAL,
} from '@atlassian/jira-software-board-common/src/constants';
import { getChildIssuesLimit as getChildIssuesLimitSelector } from './selectors';
import type { StoreApi, IssueContext } from './types';

export const setIssueContext =
	(issueContext: IssueContext) =>
	({ setState, getState }: StoreApi) => {
		const { issueKey: currentIssueKey, issueId: currentIssueId } = getState();

		// ARKEN-792: reset issueId on issueKey change if issueId isn't provided
		if (
			currentIssueId &&
			currentIssueKey &&
			!issueContext.issueId &&
			issueContext.issueKey !== currentIssueKey
		) {
			setState({
				...issueContext,
				issueId: undefined,
				viewMode: issueContext.viewMode || getState().viewMode,
			});
		} else {
			setState({
				...issueContext,
				viewMode: issueContext.viewMode || getState().viewMode,
			});
		}
	};

export const mergeIssueContext =
	(partialIssueContext: Partial<IssueContext>) =>
	({ getState, setState }: StoreApi) => {
		const { issueKey: currentIssueKey, issueId: currentIssueId } = getState();

		// ARKEN-792: reset issueId on issueKey change if issueId isn't provided
		if (
			currentIssueId &&
			currentIssueKey &&
			!partialIssueContext.issueId &&
			partialIssueContext.issueKey &&
			partialIssueContext.issueKey !== currentIssueKey
		) {
			setState({ ...getState(), ...partialIssueContext, issueId: undefined });
		} else {
			setState({ ...getState(), ...partialIssueContext });
		}
	};

export const getChildIssuesLimit =
	() =>
	({ getState }: StoreApi) =>
		getChildIssuesLimitSelector(getState());

export const getMaxTokenLength =
	() =>
	({ getState }: StoreApi) =>
		getState().maxTokenLength;

export const getIsLoadedWithPage =
	() =>
	({ getState }: StoreApi) =>
		getState().isLoadedWithPage;

export const getBackButtonUrl =
	() =>
	({ getState }: StoreApi) =>
		getState().backButtonUrl;

export const getIsSpaEnabled =
	() =>
	({ getState }: StoreApi) =>
		getState().isSpaEnabled;

export const getIssueKey =
	() =>
	({ getState }: StoreApi) =>
		getState().issueKey;

export const getIsArchived =
	() =>
	({ getState }: StoreApi) =>
		getState().isArchived;

export const setDetailViewAsModal =
	(willSave = true) =>
	({ setState }: StoreApi) => {
		if (willSave) setBoardDetailViewMode(DETAIL_VIEW_MODAL);
		setState({ viewMode: DETAIL_VIEW_MODAL });
	};

export const setDetailViewAsSidebar =
	(willSave = true) =>
	({ setState }: StoreApi) => {
		if (willSave) setBoardDetailViewMode(DETAIL_VIEW_SIDEBAR);
		setState({ viewMode: DETAIL_VIEW_SIDEBAR });
	};

export const resetDetailView =
	() =>
	({ setState }: StoreApi) => {
		setState({ viewMode: getBoardDetailViewMode() });
	};

export const actions = {
	setIssueContext,
	mergeIssueContext,
	getChildIssuesLimit,
	getMaxTokenLength,
	getIsLoadedWithPage,
	getBackButtonUrl,
	getIsSpaEnabled,
	getIssueKey,
	getIsArchived,
	setDetailViewAsModal,
	setDetailViewAsSidebar,
	resetDetailView,
} as const;
